/**
 * pad string to needed length
 *
 * @param str		- string to pad
 * @param maxLen	- max length of the string
 * @param char		- character to pad, if undefined and str is numeric - will be used "0", else " "
 *
 * @returns {string}
 */
const strPad = ( str, maxLen, char ) => {
	str = "" + str;
	if ( str.length > maxLen ) return str;

	if ( !char ) char = isNaN( Number( str ) ) ? " " : "0";

	return ( ( new Array( maxLen ).join( char ) ) + str ).slice( -maxLen );
};

/**
 * convert 2 digits year to 4 digits
 *
 * @param y2	- 2 digits year
 *
 * @returns {*}
 */
const y2y4 = y2 => {
	if ( y2 > 100 ) return y2;

	const centuryCur = Math.ceil( ( new Date() ).getFullYear() / 100 );

	return y2 + ( centuryCur - ( y2 >= 40 ) - 1 ) * 100;
};

/**
 * calculate days in the month
 *
 * @param month	- month
 * @param year	- year
 * @returns {number|number}
 */
const daysInMonth = ( month, year ) => {
	switch ( month ) {
		case 2 :
			return ( year % 400 == 0 ) || ( year % 4 == 0 && year % 100 != 0 ) ? 29 : 28;
		case 1:
		case 3:
		case 5:
		case 7:
		case 8:
		case 10:
		case 12:
			return 31;

		case 4:
		case 6:
		case 9:
		case 11:
			return 30;
	}

	return 0;
};

const types = {
	RUS:		"rus",
	RUS4Y:	"rus4y",
	ISO:		"iso",
  ENG:    "eng",
	STRUCT:	"struct",
	UTS:		"uts"
};

/**
 * convert date to Unix Time Stamp
 *
 * @param day			- day of the month
 * @param month		- month
 * @param year		- year
 * @param hours		- hours, if omitted => 0
 * @param minutes	- minutes, if omitted => 0
 * @param seconds	- seconds, if omitted => 0
 *
 * @returns {number} UTS in seconds
 */
const UTS = ( day, month, year, hours, minutes, seconds ) => {
	const d = new Date();

	if ( hours === undefined ) hours = 0;
	if ( minutes === undefined ) minutes = 0;
	if ( seconds === undefined ) seconds = 0;

	if ( day !== undefined ) {
		d.setFullYear( year, month - 1, day );
		if ( d.toString().toLowerCase().includes( "invalid" ) ) throw new Error( "uts: invalid date (y: \"" + year + "\", m: \"" + month + "\", d:\"" + day + "\")" );
		d.setHours( hours, minutes, seconds );
		if ( d.toString().toLowerCase().includes( "invalid" ) ) throw new Error( "uts: invalid time (h: \"" + hours + "\", m: \"" + minutes + "\", s:\"" + seconds + "\")" );
	}

	return Math.floor( d.getTime() / 1000 ) + timezone();
};

/**
 * parse date from string
 * the string must be a specified type, can be only time, only date or both
 *
 * @param date	- string with date
 * @param type	- type of the date from types
 *
 * @returns {
 * 	{
 * 		year: number,
 * 		month: number,
 * 		day: number,
 * 		hours:	number,
 * 		minutes: number,
 * 		seconds: number
 * 	}
 * }
 *
 * year/month/day or hours/minutes/seconds can be omitted, if not found
 *
 */
const parse = ( date, type ) => {
	let day, month, year, hours, minutes, seconds, v;

	switch ( !type ? "" : ( "" + type ).toLowerCase() ) {
		case types.RUS:
			v = /^\s*(\d{1,2})\s*\.\s*(\d{1,2})\s*\.\s*(\d{2})/g.exec( date );
			if ( v && v.length ) {
				day = Number( v[1] );
				month = Number( v[2] );
				year = y2y4( Number( v[3] ) );
			}
			break;

		case types.RUS4Y:
			v = /^\s*(\d{1,2})\s*\.\s*(\d{1,2})\s*\.\s*(\d{4})/g.exec( date );
			if ( v && v.length ) {
				day = Number( v[1] );
				month = Number( v[2] );
				year = Number( v[3] );
			}
			break;

		case types.ISO:
		case types.ENG:
			v = /^\s*(\d{4})\s*-\s*(\d{1,2})\s*-\s*(\d{1,2})/g.exec( date );
			if ( v && v.length ) {
				day = Number( v[3] );
				month = Number( v[2] );
				year = Number( v[1] );
			}
		break;

		case types.UTS:
			const d = new Date();
			const t = parseInt( date, 10 );
			if ( isNaN( t ) ) throw new Error( "parse date error: not an UTS date" );

			d.setTime( ( t - timezone() ) * 1000 );

			day = d.getDate();
			month = d.getMonth() + 1;
			year = d.getFullYear();
			hours = d.getHours();
			minutes = d.getMinutes()
			seconds = d.getSeconds();
			break;

		case types.STRUCT:
			if ( typeof( date ) != "object" ) throw new Error( "parse date error: not a struct" );

			day = parseInt( date.day, 10 );
			month = parseInt( date.month, 10 );
			year = parseInt( date.year, 10 );

			if ( isNaN( day ) || isNaN( month ) || isNaN( year ) ) throw new Error( "parse date error: invalid date" );

			if ( date.hours !== undefined ) {
				hours = date.hours;
				minutes = date.minutes;
				if ( date.seconds !== undefined ) seconds = date.seconds;
			}
			break;

		default:
			throw new Error( "parse date error: unknown type \"" + type + "\"" );
	}

	if ( year !== undefined ) {
		if ( year < 0 ) throw new Error( "parse date error: invalid year \"" + year + "\"" );
		if ( month < 1 || month > 12 ) throw new Error( "parse date error: invalid month \"" + month + "\"" );
		if ( day < 1 || day > daysInMonth( month, year ) ) throw new Error( "parse date error: invalid day \"" + day + "\" (y: \"" + year + "\", m: \"" + month + "\" )" );
	}

	v = /(\d{1,2})\s*:\s*(\d{1,2})(?:\s*:\s*(\d{1,2}))?/g.exec( date );
	if ( v && v.length ) {
		hours = Number( v[1] );
		minutes = Number( v[2] );
		seconds = v[3] === undefined ? undefined : Number( v[3] );
	}

	if ( hours !== undefined ) {
		if ( hours < 0 || hours > 23 ) throw new Error( "parse date error: invalid hours \"" + hours + "\"" );
		if ( minutes < 0 || minutes > 59 ) throw new Error( "parse date error: invalid minutes \"" + minutes + "\"" );
		if ( seconds !== undefined && seconds < 0 || seconds > 59 ) throw new Error( "parse date error: invalid seconds \"" + seconds + "\"" );
	}

	return {
		... year !== undefined && {
			day,
			month,
			year
		},
		... hours !== undefined && {
			hours,
			minutes,
			seconds
		}
	};
};

const isValid = ( date, type, withTime ) => {
	try {
		const d = parse( date, type );
		return d && d.day !== undefined && ( !withTime || d.hours !== undefined );
	}
	catch ( e ) {
	}

	return false;
};

/**
 * format string from date according specified type
 *
 * @param value		- specified date string, if omitted it uses current date
 * @param valueType	- if specified date string, it is a type of it
 * @param type		- type from types
 * @param withTime	- true for time in string
 * @param withDate	- true for date in string
 * @param toLocal	- true to convert result into current user local time
 * @returns {
 *	{
 * 		year:			number,
 * 		y:				number,
 *		month:		number,
 *		day: 			number
 *		hours:		number,
 *		minutes:	number,
 *		seconds:	number,
 *		tz:				number,
 *		UTS:			number
 *	} |
 * 	string
 * }
 *
 * y - 2 digit year
 * tz - time zone shift in seconds
 *
 */
const format = ( value, valueType, type, withTime, withDate, toLocal ) => {
	if ( withDate === undefined ) withDate = true;
	if ( withTime === undefined ) withTime = false;
	if ( !type ) type = types.STRUCT;

	const z = timezone();

	const d = new Date();

	if ( value ) {
		const t = parse( value, valueType );
		d.setTime( ( UTS( t.day, t.month, t.year, t.hours, t.minutes, t.seconds ) - z ) * 1000 );
	}

	if ( toLocal ) d.setTime( d.getTime() + z * 1000 );

	const y = d.getFullYear();
	const t = {
		day:		d.getDate(),
		month:	d.getMonth() + 1,
		year:		y,
		y:			Number( y.toString().slice( -2 ) ),
		hours:	d.getHours(),
		minutes:d.getMinutes(),
		seconds:d.getSeconds(),
		UTS:		Math.floor( d.getTime() / 1000 ) + z,
		tz:     z
	};

	let r = "";

	if ( withDate ) {
		switch ( !type ? "" : ( "" + type ).toLowerCase() ) {
			case types.STRUCT:
				return t;

			case types.ISO:
				r += t.year + "-" + strPad( t.month, 2 ) + "-" + strPad( t.day, 2 );
				if ( withTime ) r += "T";
			break;

			case types.ENG:
				r += t.year + "-" + strPad( t.month, 2 ) + "-" + strPad( t.day, 2 );
				if ( withTime ) r += " ";
			break;

			case types.RUS4Y:
				r += strPad( t.day, 2 ) + "." + strPad( t.month, 2 ) + "." + t.year;
				if ( withTime ) r += " ";
				break;

			case types.UTS:
				r = t.UTS;
				withTime = false;
				break;

			case types.RUS:
			default:
				r += strPad( t.day, 2 ) + "." + strPad( t.month, 2 ) + "." + strPad( t.y, 2 );
				if ( withTime ) r += " ";
		}
	}

	if ( withTime ) r += strPad( t.hours, 2 ) + ":" + strPad( t.minutes, 2 ) + ":" + strPad( t.seconds, 2 );

	return r;
};

const timezone = () => {
	return -( new Date() ).getTimezoneOffset() * 60;
};

const timezoneRemove = ( date, dateType, withDate, withTime ) => {
	const t = format( date, dateType, types.STRUCT, withTime, withDate );
	return format( t.UTS - timezone(), types.UTS, dateType, withTime, withDate );
};

const timezoneAdd = ( date, dateType, withDate, withTime ) => {
	const t = format( date, dateType, types.STRUCT, withTime, withDate );
	return format( t.UTS + timezone(), types.UTS, dateType, withTime, withDate );
};

const check = ( date, type, withTime ) => {
	date = ( date + "" ).trim();
	if ( date ) {
		const t = parse( date, type );
		if ( t.day && ( !withTime || t.seconds !== undefined ) ) return true;
	}
	
	return false;
};

/**
 * check formatted RUS date, remove timezone and format it to RUS again
 *
 * if "date" is empty, or consist of only spaces - returns ""
 * if "date" is not date - return ""
 * if no time is parsed, but date contains other symbols except date symbols - returns ""
 *
 * @param string	date
 * @param integer tz		if undefined = local timezone
 * @param integer asUTS	if true => returned value will be UTS
 *
 * @returns string
 */
const checkRUSremTZ = ( date, tz, asUTS ) => {
	if ( !date || /^\s*$/.test( date ) ) return "";

	try {
		const t = parse( date, types.RUS );
		if (
			!t.day ||
			t.day && t.seconds === undefined && !/^\s*\d{1,2}\.\d{1,2}\.\d{2}\s*$/.test( date )
		) return "";

		const d = new Date();
		const z = timezone();
		if ( tz === undefined ) tz = z;
		d.setTime( ( UTS( t.day, t.month, t.year, t.hours, t.minutes, t.seconds ) - z - tz ) * 1000 );
		const uts = Math.floor( d.getTime() / 1000 + z );

		if ( asUTS ) return uts;

		return format( uts, types.UTS, types.RUS, t.seconds !== undefined, true );
	}
	catch ( e ) {}

	return "";
};
/*
module.exports = {
  y2y4,
  UTS,
  types,
  parse,
  format,
  daysInMonth,
  isValid,
  timezone,
  timezoneRemove,
  timezoneAdd,

  check,
  checkRUSremTZ
};
/**/
/**/
export default {
  y2y4,
  UTS,
  types,
  parse,
  format,
  daysInMonth,
  isValid,
  timezone,
  timezoneRemove,
  timezoneAdd,

  check,
  checkRUSremTZ
}
/**/
